import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import s from "./styles.module.scss";
import chevronDownIcon from "../../assets/chewron_down.svg";

const CustomDropdown = ({
  options = [],
  value = "",
  onChange,
  placeholder = "Select option",
  className = "",
  hasError = false,
  disabled = false,
  borderColor = null,
  displayValue = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownListRef.current &&
        !dropdownListRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    if (disabled) return;
    onChange(option);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const selectedOption = options.find((opt) => opt === value);
  const triggerDisplayValue = displayValue || selectedOption || placeholder;

  const rootClass = [
    s.dropdown,
    isOpen && s.dropdownOpen,
    hasError && s.dropdownError,
    disabled && s.dropdownDisabled,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  
  const dropdownStyle = borderColor ? { borderColor: borderColor, borderWidth: '2px' } : {};

  // Add this function to convert hex to rgba
  const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Create trigger styles with hover effect
  const triggerStyle = borderColor ? {
    '--hover-border-color': borderColor,
    '--hover-bg-color': hexToRgba(borderColor, 0.05)
  } : {};

  // In CustomDropdown component, add this to create option styles
const optionStyle = (isSelected) => {
  if (borderColor) {
    return {
      backgroundColor: isSelected ? hexToRgba(borderColor, 0.15): undefined, // Light tint for selected
      '--hover-bg-color': hexToRgba(borderColor, 0.025) // Even lighter on hover
    };
  }
  return {};
};

  return (
    <>
      <div ref={dropdownRef} className={rootClass} style={dropdownStyle}>
        <button
          ref={triggerRef}
          type="button"
          className={s.dropdownTrigger}
          style={triggerStyle}
          onClick={toggleDropdown}
          aria-expanded={isOpen}
          disabled={disabled}
        >
          <span
            className={`${s.dropdownValue} ${
              !selectedOption ? s.dropdownPlaceholder : ""
            }`}
          >
            {triggerDisplayValue}
          </span>
          <span className={s.dropdownArrow}>
            <img src={chevronDownIcon} alt="Dropdown arrow" />
          </span>
        </button>
      </div>

      {isOpen &&
        !disabled &&
        ReactDOM.createPortal(
          <div
            ref={dropdownListRef}
            style={menuStyle}
            className={s.dropdownList}
          >
            {options.map((option, index) => (
              <button
                key={index}
                type="button"
                className={`${s.dropdownOption} ${
                  option === value ? s.dropdownOptionSelected : ""
                }`}
                style={optionStyle(option === value)} // Add this
                onClick={() => handleSelect(option)}
              >
                {option}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default CustomDropdown;
